// src/utils/templateDefaults.js
/**
 * Template Defaults Utility
 * Provides default content for template creation based on trigger events
 */

/**
 * Available template triggers for different template types
 */
export const TEMPLATE_TRIGGERS = {
  email: [
    { key: 'appointment_confirmation', label: 'Appointment Confirmation' },
    { key: 'appointment_reminder', label: 'Appointment Reminder' },
    { key: 'appointment_cancellation', label: 'Appointment Cancellation' },
    { key: 'appointment_reschedule', label: 'Appointment Reschedule' },
    { key: 'invoice_notification', label: 'Invoice Notification' },
    { key: 'payment_confirmation', label: 'Payment Confirmation' },
    { key: 'service_completion', label: 'Service Completion' },
    { key: 'follow_up', label: 'Follow Up' }
  ],
  sms: [
    { key: 'appointment_confirmation', label: 'Appointment Confirmation' },
    { key: 'appointment_reminder', label: 'Appointment Reminder' },
    { key: 'appointment_cancellation', label: 'Appointment Cancellation' },
    { key: 'on_route_notification', label: 'On Route Notification' },
    { key: 'service_completion', label: 'Service Completion' },
    { key: 'payment_reminder', label: 'Payment Reminder' }
  ]
};

/**
 * Default content for email templates by trigger
 */
const EMAIL_DEFAULTS = {
  appointment_confirmation: {
    subject: "Your {{service_type}} appointment is confirmed",
    body_html: `
      <h2>Appointment Confirmed</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>Your <strong>{{service_type}}</strong> appointment has been confirmed for:</p>
      
      <p><strong>Date:</strong> {{appointment_date}}<br>
      <strong>Time:</strong> {{appointment_time}}<br>
      <strong>Location:</strong> {{service_address}}</p>
      
      <p>We look forward to providing you with excellent service. If you need to make any changes or have questions, please don't hesitate to contact us.</p>
      
      <p>Thank you for choosing our services!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, Your {{service_type}} appointment is confirmed for {{appointment_date}} at {{appointment_time}} at {{service_address}}. We look forward to serving you! If you have any questions, please contact us. Thank you for choosing our services!"
  },

  appointment_reminder: {
    subject: "Reminder: {{service_type}} appointment {{appointment_date}}",
    body_html: `
      <h2>Appointment Reminder</h2>
      
      <p>Hello {{customer_name}},</p>
      
      <p>This is a friendly reminder about your upcoming appointment:</p>
      
      <p><strong>Service:</strong> {{service_type}}<br>
      <strong>Date:</strong> {{appointment_date}}<br>
      <strong>Time:</strong> {{appointment_time}}<br>
      <strong>Location:</strong> {{service_address}}</p>
      
      <p>Please ensure someone is available at the scheduled time. If you need to reschedule, please contact us as soon as possible.</p>
      
      <p>We look forward to seeing you!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Hello {{customer_name}}, This is a reminder about your {{service_type}} appointment on {{appointment_date}} at {{appointment_time}} at {{service_address}}. Please ensure someone is available. Contact us if you need to reschedule. See you soon!"
  },

  appointment_cancellation: {
    subject: "Appointment Cancellation - {{service_type}}",
    body_html: `
      <h2>Appointment Cancelled</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>We're writing to inform you that your appointment has been cancelled:</p>
      
      <p><strong>Service:</strong> {{service_type}}<br>
      <strong>Originally scheduled:</strong> {{appointment_date}} at {{appointment_time}}<br>
      <strong>Location:</strong> {{service_address}}</p>
      
      <p>If you would like to reschedule, please contact us and we'll be happy to find a new time that works for you.</p>
      
      <p>We apologize for any inconvenience this may cause.</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, Your {{service_type}} appointment scheduled for {{appointment_date}} at {{appointment_time}} has been cancelled. Please contact us if you'd like to reschedule. We apologize for any inconvenience."
  },

  appointment_reschedule: {
    subject: "Appointment Rescheduled - {{service_type}}",
    body_html: `
      <h2>Appointment Rescheduled</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>Your appointment has been rescheduled to a new time:</p>
      
      <p><strong>Service:</strong> {{service_type}}<br>
      <strong>New Date:</strong> {{appointment_date}}<br>
      <strong>New Time:</strong> {{appointment_time}}<br>
      <strong>Location:</strong> {{service_address}}</p>
      
      <p>Please make note of the new appointment time. If this doesn't work for you, please contact us to discuss other options.</p>
      
      <p>Thank you for your understanding!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, Your {{service_type}} appointment has been rescheduled to {{appointment_date}} at {{appointment_time}} at {{service_address}}. Please contact us if this time doesn't work. Thank you!"
  },

  invoice_notification: {
    subject: "Invoice #{{invoice_number}} from {{business_name}}",
    body_html: `
      <div style="font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 30px; padding: 20px;">
          <div style="width: 48px; height: 48px; background: #10b981; border-radius: 8px; margin: 0 auto 12px; display: inline-flex; align-items: center; justify-content: center;">
            <span style="color: white; font-weight: bold; font-size: 18px;">{{business_initials}}</span>
          </div>
          <h1 style="color: #1f2937; font-size: 24px; font-weight: bold; margin: 0;">Invoice #{{invoice_number}}</h1>
        </div>

        <!-- Greeting -->
        <p style="color: #374151; margin: 0 0 16px 0;">Hello {{customer_name}},</p>
        
        <p style="color: #374151; margin: 0 0 24px 0;">
          Thank you for choosing {{business_name}}. Please find your invoice details below:
        </p>
        
        <!-- Invoice Summary Box -->
        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 24px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px 0; width: 50%; vertical-align: top;">
                <div style="color: #6b7280; font-size: 14px;">Invoice Number</div>
                <div style="color: #1f2937; font-weight: 600;">{{invoice_number}}</div>
              </td>
              <td style="padding: 8px 0; width: 50%; vertical-align: top;">
                <div style="color: #6b7280; font-size: 14px;">Invoice Date</div>
                <div style="color: #1f2937; font-weight: 600;">{{invoice_date}}</div>
              </td>
            </tr>
            <tr>
              <td style="padding: 8px 0; width: 50%; vertical-align: top;">
                <div style="color: #6b7280; font-size: 14px;">Due Date</div>
                <div style="color: #1f2937; font-weight: 600;">{{due_date}}</div>
              </td>
              <td style="padding: 8px 0; width: 50%; vertical-align: top;">
                <div style="color: #6b7280; font-size: 14px;">Total Amount</div>
                <div style="color: #10b981; font-weight: 600; font-size: 18px;">$` + `{{total_amount}}</div>
              </td>
            </tr>
          </table>
        </div>

        <!-- Services Provided -->
        <div style="margin-bottom: 24px;">
          <h3 style="color: #1f2937; font-weight: 600; margin: 0 0 8px 0;">Services Provided:</h3>
          <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 4px; padding: 12px;">
            <pre style="color: #374151; font-size: 14px; white-space: pre-wrap; font-family: 'Courier New', monospace; margin: 0;">{{line_items_summary}}</pre>
          </div>
        </div>

        <!-- Amount Breakdown -->
        <div style="background: #ecfdf5; border: 1px solid #d1fae5; border-radius: 8px; padding: 16px; margin-bottom: 24px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="color: #374151; padding: 4px 0;">Subtotal:</td>
              <td style="color: #374151; text-align: right; padding: 4px 0;">$` + `{{subtotal}}</td>
            </tr>
            <tr>
              <td style="color: #374151; padding: 4px 0;">Tax:</td>
              <td style="color: #374151; text-align: right; padding: 4px 0;">$` + `{{tax_amount}}</td>
            </tr>
            <tr style="border-top: 1px solid #d1fae5;">
              <td style="color: #1f2937; font-weight: 600; font-size: 18px; padding: 8px 0 4px 0;">Total Amount:</td>
              <td style="color: #10b981; font-weight: bold; font-size: 20px; text-align: right; padding: 8px 0 4px 0;">$` + `{{total_amount}}</td>
            </tr>
          </table>
        </div>

        <!-- Payment Button -->
        <div style="text-align: center; margin: 24px 0;">
          <table cellpadding="0" cellspacing="0" style="margin: 0 auto;">
            <tr>
              <td style="background-color: #10b981; border-radius: 8px; padding: 16px 32px;">
                <a href="{{payment_url}}" style="color: white; text-decoration: none; font-weight: 600; font-size: 16px; display: block;">
                  Pay Invoice Online
                </a>
              </td>
            </tr>
          </table>
          <p style="color: #6b7280; font-size: 14px; margin: 8px 0 0 0;">
            Click to pay securely with credit card
          </p>
        </div>

        <!-- Payment URL -->
        <div style="background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; padding: 16px; margin-bottom: 24px;">
          <p style="color: #374151; font-size: 14px; margin: 0 0 8px 0;">
            You can also view and pay your invoice online at:
          </p>
          <p style="color: #2563eb; font-size: 14px; font-family: 'Courier New', monospace; word-break: break-all; margin: 0;">
            {{payment_url}}
          </p>
        </div>

        <!-- Notes (if any) -->
        <div style="margin-bottom: 24px;">
          <h4 style="color: #1f2937; font-weight: 500; margin: 0 0 8px 0;">Additional Notes:</h4>
          <div style="background: #fef3c7; border: 1px solid #fde68a; border-radius: 4px; padding: 12px;">
            <p style="color: #374151; font-size: 14px; white-space: pre-wrap; margin: 0;">{{notes}}</p>
          </div>
        </div>

        <!-- Contact Information -->
        <div style="border-top: 1px solid #e5e7eb; padding-top: 16px; margin-bottom: 16px;">
          <p style="color: #374151; margin: 0 0 8px 0;">
            If you have any questions about this invoice, please contact us:
          </p>
          <div style="color: #6b7280; font-size: 14px;">
            <p style="margin: 0;">Phone: {{business_phone}}</p>
            <p style="margin: 0;">Email: {{business_email}}</p>
          </div>
        </div>

        <!-- Closing -->
        <div style="color: #374151;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="font-weight: 600; margin: 0;">{{business_name}}</p>
        </div>

      </div>
    `,
    body_text: "Hello {{customer_name}}, Thank you for choosing {{business_name}}. Your invoice #{{invoice_number}} for ${{total_amount}} is ready. Due date: {{due_date}}. You can view and pay online at: {{payment_url}}. Contact us at {{business_phone}} or {{business_email}} with any questions. Thank you for your business!"
  },

  payment_confirmation: {
    subject: "Payment Confirmation - Thank you!",
    body_html: `
      <h2>Payment Received</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>Thank you! We've successfully received your payment for:</p>
      
      <p><strong>Service:</strong> {{service_type}}<br>
      <strong>Service Date:</strong> {{appointment_date}}<br>
      <strong>Payment Date:</strong> {{payment_date}}</p>
      
      <p>Your account is now up to date. A receipt has been generated and is available in your account.</p>
      
      <p>We appreciate your prompt payment and look forward to serving you again!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, Thank you! We've received your payment for {{service_type}}. Your account is up to date and a receipt is available. We appreciate your business!"
  },

  service_completion: {
    subject: "Service Complete - {{service_type}}",
    body_html: `
      <h2>Service Completed</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>We've successfully completed your {{service_type}} service today. Here's a summary:</p>
      
      <p><strong>Service:</strong> {{service_type}}<br>
      <strong>Completion Date:</strong> {{appointment_date}}<br>
      <strong>Location:</strong> {{service_address}}</p>
      
      <p>Our team has completed the work according to your specifications. If you have any questions about the service performed, please don't hesitate to reach out.</p>
      
      <p>Thank you for choosing our services. We hope to work with you again soon!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, We've completed your {{service_type}} service at {{service_address}}. The work was finished on {{appointment_date}}. Contact us if you have any questions. Thank you!"
  },

  follow_up: {
    subject: "How was your {{service_type}} experience?",
    body_html: `
      <h2>How did we do?</h2>
      
      <p>Dear {{customer_name}},</p>
      
      <p>We hope you're satisfied with the {{service_type}} service we recently completed at {{service_address}}.</p>
      
      <p>Your feedback is important to us and helps us continue to improve our services. We'd love to hear about your experience!</p>
      
      <p><strong>Leave us a review or let us know how we did!</strong></p>
      
      <p>If you have any concerns or questions about the service, please don't hesitate to contact us directly.</p>
      
      <p>Thank you for choosing our services!</p>
      
      <p>Best regards,<br>The Team</p>
    `,
    body_text: "Dear {{customer_name}}, We hope you're satisfied with the {{service_type}} service at {{service_address}}. Your feedback is important to us! Please let us know how we did. Thank you!"
  }
};

/**
 * Default content for SMS templates by trigger
 */
const SMS_DEFAULTS = {
  appointment_confirmation: {
    message_body: "Hi {{customer_name}}! Your {{service_type}} appointment is confirmed for {{appointment_date}} at {{appointment_time}} at {{service_address}}. See you then!"
  },

  appointment_reminder: {
    message_body: "Reminder: Your {{service_type}} appointment is tomorrow at {{appointment_time}} at {{service_address}}. Please ensure someone is available. Contact us if you need to reschedule."
  },

  appointment_cancellation: {
    message_body: "Hi {{customer_name}}, your {{service_type}} appointment for {{appointment_date}} has been cancelled. Please contact us to reschedule. Sorry for any inconvenience!"
  },

  on_route_notification: {
    message_body: "Hi {{customer_name}}! Our team is now on route to {{service_address}} for your {{service_type}} appointment. We'll be there within the scheduled time window."
  },

  service_completion: {
    message_body: "Hi {{customer_name}}! We've completed your {{service_type}} service at {{service_address}}. Thank you for choosing our services! Contact us if you have any questions."
  },

  payment_reminder: {
    message_body: "Hi {{customer_name}}, this is a friendly reminder that payment for your {{service_type}} service is due. You can pay online or contact us for assistance. Thank you!"
  }
};

/**
 * Generate default content for a template based on trigger and type
 * @param {string} triggerKey - The trigger event key
 * @param {string} templateType - 'email' or 'sms'
 * @returns {object} Default content object
 */
export const generateDefaultContent = (triggerKey, templateType) => {
  if (templateType === 'email') {
    return EMAIL_DEFAULTS[triggerKey] || {
      subject: "New Email Template",
      body_html: "<p>Dear {{customer_name}},</p><p>Your content here...</p><p>Best regards,<br>The Team</p>",
      body_text: "Dear {{customer_name}}, Your content here... Best regards, The Team"
    };
  } else if (templateType === 'sms') {
    return SMS_DEFAULTS[triggerKey] || {
      message_body: "Hi {{customer_name}}, your message here..."
    };
  }
  
  return {};
};

/**
 * Create a temporary template object with default structure
 * @param {string} templateType - 'email' or 'sms'
 * @returns {object} Temporary template object
 */
export const createTempTemplate = (templateType) => {
  const now = new Date().toISOString();
  
  return {
    id: `temp-${Date.now()}`, // Unique temporary ID
    template_key: '', // Empty until trigger selected
    name: `New ${templateType === 'email' ? 'Email' : 'SMS'} Template`,
    active: false,
    created_at: now,
    updated_at: now,
    isTemporary: true, // Flag for identification
    
    // Type-specific fields with empty defaults
    ...(templateType === 'email' ? {
      subject: '',
      body_html: '',
      body_text: ''
    } : {
      message_body: ''
    })
  };
};

/**
 * Get available triggers for a template type
 * @param {string} templateType - 'email' or 'sms'
 * @returns {Array} Array of trigger options
 */
export const getAvailableTriggers = (templateType) => {
  return TEMPLATE_TRIGGERS[templateType] || [];
};

/**
 * Get human-readable label for a trigger key
 * @param {string} triggerKey - The trigger key
 * @param {string} templateType - 'email' or 'sms'
 * @returns {string} Human-readable label
 */
export const getTriggerLabel = (triggerKey, templateType = 'email') => {
  const triggers = getAvailableTriggers(templateType);
  const trigger = triggers.find(t => t.key === triggerKey);
  return trigger ? trigger.label : triggerKey;
};

/**
 * Available template variables that can be used in templates
 */
export const TEMPLATE_VARIABLES = [
  { key: 'customer_name', label: 'Customer Name', description: 'The customer\'s full name' },
  { key: 'service_type', label: 'Service Type', description: 'The type of service being provided' },
  { key: 'appointment_date', label: 'Appointment Date', description: 'The scheduled appointment date' },
  { key: 'appointment_time', label: 'Appointment Time', description: 'The scheduled appointment time' },
  { key: 'service_address', label: 'Service Address', description: 'The address where service is performed' },
  { key: 'payment_date', label: 'Payment Date', description: 'The date payment was received' },
  { key: 'invoice_number', label: 'Invoice Number', description: 'The invoice number' },
  { key: 'company_name', label: 'Company Name', description: 'Your business name' },
  { key: 'company_phone', label: 'Company Phone', description: 'Your business phone number' },
  // NEW: Invoice-specific variables
  { key: 'business_name', label: 'Business Name', description: 'Your business name' },
  { key: 'business_email', label: 'Business Email', description: 'Your business email address' },
  { key: 'business_phone', label: 'Business Phone', description: 'Your business phone number' },
  { key: 'business_initials', label: 'Business Initials', description: 'Your business initials for logo' },
  { key: 'invoice_date', label: 'Invoice Date', description: 'The date the invoice was created' },
  { key: 'due_date', label: 'Due Date', description: 'The invoice due date' },
  { key: 'total_amount', label: 'Total Amount', description: 'The total invoice amount' },
  { key: 'subtotal', label: 'Subtotal', description: 'The invoice subtotal before tax' },
  { key: 'tax_amount', label: 'Tax Amount', description: 'The tax amount' },
  { key: 'line_items_summary', label: 'Line Items Summary', description: 'Summary of all invoice line items' },
  { key: 'payment_url', label: 'Payment URL', description: 'Link to pay the invoice online' },
  { key: 'notes', label: 'Invoice Notes', description: 'Additional notes on the invoice' }
];